import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import Styles from './vm-video-comment-attachment-thumbnail.scss';

interface LightboxShowDetail {
    src: string;
    alt: string;
}

/**
 * Inline image attachment thumbnail rendered inside a posted comment.
 *
 * Behaves as a button: click (or Enter / Space) dispatches `vm-image-lightbox:open`
 * carrying the full-resolution `src` + `alt`. The thumbnail itself preserves
 * the image's aspect ratio under a fixed `max-height` and lazy-loads via the
 * native `loading="lazy"` attribute.
 *
 * The thumbnail never opens a lightbox itself — it just announces the intent
 * via the event. A page-level `<vm-image-lightbox>` mounted in the consuming
 * template handles the event and renders the full-resolution view, so a
 * single shared lightbox serves every thumbnail (and any other surface that
 * dispatches the same event).
 *
 * @attr src       - Full-resolution image URL. Sent in the event detail and
 *                    used for the inline thumb if `thumb-src` is omitted.
 * @attr alt       - Alt text describing the image (for screen readers).
 * @attr thumb-src - Optional smaller thumbnail URL; the event still carries
 *                    `src` so the lightbox shows the full-res image.
 *
 * @csspart button  - The clickable wrapper around the image.
 * @csspart image   - The thumbnail `<img>` element.
 *
 * @fires vm-image-lightbox:open - Bubbles + composed + cancelable. Detail:
 *   `{ src: string, alt: string }`. Call `preventDefault()` to opt out of
 *   the lightbox (e.g. open in a new tab instead).
 *
 * @example
 * ```html
 * <!-- Mount one page-level lightbox in the template; it picks up the event. -->
 * <vm-image-lightbox></vm-image-lightbox>
 *
 * <vm-video-comment-attachment-thumbnail
 *   src="/media/comments/photo.jpg"
 *   alt="X-ray of left knee, AP view"
 * ></vm-video-comment-attachment-thumbnail>
 * ```
 */
@customElement('vm-video-comment-attachment-thumbnail')
export class VmVideoCommentAttachmentThumbnail extends LitElement {
    static override styles = Styles;

    /** Full-resolution image URL — sent in the `vm-image-lightbox:open` event detail. */
    @property()
    src = '';

    /** Alt text. */
    @property()
    alt = '';

    /** Optional smaller thumbnail URL; if omitted, `src` is used for the inline image too. */
    @property({ attribute: 'thumb-src' })
    thumbSrc = '';

    private _onClick = () => {
        this.dispatchEvent(
            new CustomEvent<LightboxShowDetail>('vm-image-lightbox:open', {
                detail: { src: this.src, alt: this.alt },
                bubbles: true,
                composed: true,
                cancelable: true,
            }),
        );
    };

    override render() {
        const thumb = this.thumbSrc || this.src;
        return html`
            <button
                type="button"
                class="vm-video-comment-attachment-thumbnail"
                part="button"
                aria-label=${this.alt || 'Open image'}
                @click=${this._onClick}
            >
                <img
                    class="vm-video-comment-attachment-thumbnail__image"
                    part="image"
                    src=${thumb}
                    alt=${this.alt}
                    loading="lazy"
                    decoding="async"
                />
            </button>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-comment-attachment-thumbnail': VmVideoCommentAttachmentThumbnail;
    }
    interface HTMLElementEventMap {
        'vm-image-lightbox:open': CustomEvent<LightboxShowDetail>;
    }
}
